import React from "react";
import { Loader2, CheckCircle, AlertTriangle, TrendingUp, BarChart3 } from "lucide-react";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";

const getScoreColor = (score) => {
  if (score >= 8) return "text-emerald-600";
  if (score >= 5) return "text-amber-600";
  return "text-red-600";
};

const getGradeBg = (grade) => {
  if (grade === "A" || grade === "A+") return "bg-emerald-100 text-emerald-700";
  if (grade === "B" || grade === "B+") return "bg-cyan-100 text-cyan-700";
  if (grade === "C") return "bg-amber-100 text-amber-700";
  return "bg-red-100 text-red-700";
};

export function BenchmarkDialog({ open, onOpenChange, loading, result, clause }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BarChart3 size={18} className="text-cyan-600" /> Clause Benchmark</DialogTitle>
          <DialogDescription>{clause?.name} ({clause?.category})</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <Loader2 size={32} className="animate-spin text-cyan-500" />
            <p className="text-sm text-slate-500">Analyzing against best-in-class standards...</p>
          </div>
        ) : result ? (
          <div className="space-y-5">
            <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-md">
              <div className="text-center">
                <p className={`text-4xl font-bold ${getScoreColor(result.overall_score)}`} data-testid="benchmark-score">{result.overall_score}</p>
                <p className="text-xs text-slate-400 mt-0.5">out of 10</p>
              </div>
              <div className="flex-1">
                <Badge className={`${getGradeBg(result.grade)} rounded-sm text-sm px-3 py-1`} data-testid="benchmark-grade">Grade: {result.grade}</Badge>
                {result.best_practice_summary && <p className="text-xs text-slate-500 mt-2 leading-relaxed">{result.best_practice_summary}</p>}
              </div>
            </div>

            {result.criteria && (
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Criteria Scores</p>
                <div className="grid grid-cols-2 gap-2">
                  {result.criteria.map((c, i) => (
                    <div key={c.name || i} className="flex items-center gap-2 p-2.5 border rounded-sm bg-white dark:bg-slate-900">
                      <span className={`text-lg font-bold w-8 text-center ${getScoreColor(c.score)}`}>{c.score}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-slate-700 dark:text-slate-300">{c.name}</p>
                        <p className="text-[11px] text-slate-400 truncate">{c.comment}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.strengths?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Strengths</p>
                <div className="space-y-1.5">
                  {result.strengths.map((s, i) => (
                    <div key={`strength-${i}-${s.slice(0,20)}`} className="flex items-start gap-2 text-sm">
                      <CheckCircle size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" /><span className="text-slate-600 dark:text-slate-300">{s}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.gaps?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Gaps Identified</p>
                <div className="space-y-1.5">
                  {result.gaps.map((g, i) => (
                    <div key={`gap-${i}-${g.slice(0,20)}`} className="flex items-start gap-2 text-sm">
                      <AlertTriangle size={14} className="text-amber-500 mt-0.5 flex-shrink-0" /><span className="text-slate-600 dark:text-slate-300">{g}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.suggested_improvements?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Suggested Improvements</p>
                <div className="space-y-2">
                  {result.suggested_improvements.map((s, i) => (
                    <div key={`imp-${i}-${s.title}`} className="flex items-start gap-2 p-2.5 border rounded-sm">
                      <TrendingUp size={14} className="text-blue-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{s.title}</p>
                          <Badge className={`rounded-sm text-[10px] ${s.priority === "high" ? "bg-red-100 text-red-700" : s.priority === "medium" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{s.priority}</Badge>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">{s.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="text-[11px] text-slate-400 text-center pt-2 border-t">
              Benchmarked {result.benchmarked_at ? new Date(result.benchmarked_at).toLocaleString() : "just now"}
            </p>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
